import { useParams, Link, Navigate } from "react-router-dom";
import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import { useQuery } from "@apollo/client/react";
import { Tabs } from "@mantine/core";
import { gql } from "@/__generated__";
import { getMedicalInfoTabsForCondition } from "shared/src/data/medical-info-tabs";
import Navbar from "@/components/layout/navbar/navbar";
import { ReferPatientProgressTracker } from "@/components/refer-patient-progress-tracker";
import { StrengthenReferral } from "../strengthen-referral/strengthen-referral";
import { PageFooter } from "@/components/layout/page-footer";
import {
  FilteredSurveyTab,
  FilteredSurveyTabHandle,
} from "../strengthen-referral/components/tabs/filtered-survey-tab/filtered-survey-tab";
import { Toast } from "design-system/src/components/toast";
import styles from "./medical-info.module.scss";
import { flushSync } from "react-dom";
import { useFormContext } from "@/contexts/form-context";
import { useHasSurveySchema } from "@/hooks/use-has-survey-schema";

type SaveStatus = "success" | "error" | null;

const GET_APPEAL_FOR_MEDICAL_INFO = gql(`
  query GetAppealForMedicalInfo($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      appealId
      status {
        referred
      }
      denial {
        condition
      }
    }
  }
`);

interface MedicalInfoTabProps {
  tabId: string;
}

export function MedicalInfoTab({ tabId }: MedicalInfoTabProps) {
  const { appealId } = useParams<{ appealId: string }>();
  const surveyRef = useRef<FilteredSurveyTabHandle>(null);
  const { resetDirty } = useFormContext();
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(null);

  useEffect(() => {
    if (!saveStatus) return;
    const timer = setTimeout(() => setSaveStatus(null), 3000);
    return () => clearTimeout(timer);
  }, [saveStatus]);

  const dismissSaveStatus = useCallback(() => setSaveStatus(null), []);

  const { data, loading: statusLoading } = useQuery(
    GET_APPEAL_FOR_MEDICAL_INFO,
    {
      variables: { appealId: appealId! },
      skip: !appealId,
      fetchPolicy: "no-cache",
    },
  );

  const condition = data?.getAppealById?.denial?.condition;
  const readOnly = data?.getAppealById?.status?.referred ?? false;

  const { hasSurveySchema, loading: schemaLoading } =
    useHasSurveySchema(appealId);

  const tabs = useMemo(
    () => getMedicalInfoTabsForCondition(condition),
    [condition],
  );
  const currentTab = tabs.find((t) => t.id === tabId);

  if (!appealId) {
    return null;
  }

  if (!schemaLoading && !hasSurveySchema) {
    return <Navigate to={`/appeals/${appealId}/strengthen/patient`} replace />;
  }

  if (!statusLoading && !currentTab && tabs.length > 0) {
    return (
      <Navigate
        to={`/appeals/${appealId}/strengthen/medical-info/${tabs[0].route}`}
        replace
      />
    );
  }

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const success = await surveyRef.current?.save();
      if (success) {
        flushSync(() => {
          resetDirty();
        });
        setSaveStatus("success");
      } else {
        setSaveStatus("error");
      }
      return success ?? false;
    } catch {
      setSaveStatus("error");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBeforeSend = async () => {
    return await handleSave();
  };

  const footer =
    !statusLoading && !readOnly ? (
      <PageFooter
        primaryButton={{
          text: "Save",
          onClick: handleSave,
          loading: isSaving,
          disabled: isSaving,
        }}
      />
    ) : undefined;

  const tabContent = (
    <div className={styles.container}>
      <div className={styles.sideTabsContainer}>
        <Tabs value={tabId} orientation="vertical" className={styles.sideTabs}>
          <Tabs.List>
            {tabs.map((tab) => (
              <Tabs.Tab
                key={tab.id}
                value={tab.id}
                renderRoot={(props) => (
                  <Link
                    {...props}
                    to={`/appeals/${appealId}/strengthen/medical-info/${tab.route}`}
                  />
                )}
              >
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </div>
      <div className={styles.contentContainer}>
        <div className={styles.saveToast}>
          <Toast
            variant={saveStatus ?? "success"}
            onDismiss={dismissSaveStatus}
            isVisible={!!saveStatus}
          >
            {saveStatus === "success"
              ? "Your answers have been saved."
              : "Failed to save. Please try again."}
          </Toast>
        </div>
        {currentTab && (
          <FilteredSurveyTab
            key={currentTab.surveyTag}
            ref={surveyRef}
            appealId={appealId}
            readOnly={readOnly}
            filterTag={currentTab.surveyTag}
          />
        )}
      </div>
    </div>
  );

  return (
    <Navbar
      titleComponent={
        <ReferPatientProgressTracker
          appealId={appealId}
          onBeforeSendReferral={handleSaveBeforeSend}
        />
      }
      footer={footer}
    >
      <StrengthenReferral
        appealId={appealId}
        activeTab="medical-info"
        tabContent={tabContent}
      />
    </Navbar>
  );
}
